// /web/src/pages/calculators/UValues.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useApi } from "../../lib/api";
import { trackCalculatorRun } from "../../lib/amplitude";
import CalculatorLayout, {
  CalculatorSection,
} from "../../components/calculators/CalculatorLayout.jsx";
import DownloadUValueReportBtn from "../../components/pdf/DownloadUValueReportBtn.jsx";
import useUsageEvent from "../../hooks/useUsageEvent";

function toErrorMessage(err) {
  if (!err) return "Unknown error";
  if (typeof err === "string") return err;
  if (err.detail) {
    if (typeof err.detail === "string") return err.detail;
    if (Array.isArray(err.detail) && err.detail[0]?.msg) return err.detail[0].msg;
  }
  if (typeof err.message === "string" && err.message !== "[object Object]") return err.message;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

const U_VALUE_CALCULATOR_META = {
  title: "U-value calculator",
  subtitle: "Select a project and assemble the layered build-up to estimate thermal transmittance.",
  notes: [
    "Surface resistances follow BS EN ISO 6946 internal and external film assumptions for each component type.",
    "Lambda (λ) values are indicative only and should be replaced with manufacturer data for certified assessments.",
    "Well ventilated air layers omit external resistances; slightly ventilated layers include a reduced resistance value.",
    "This estimator excludes repeating thermal bridges; verify the final U-value with a qualified professional before issuing.",
  ],
};

/* ----------------------- Data ----------------------- */
// --- Data: categories and materials (λ in W/m·K) ---
const materialsByCategory = {
  "Cavity": [
    { name: "Foil lined", lambda: 0.025 },
    { name: "Unlined", lambda: 0.025 },
  ],

  "Concrete": [
    { name: "In-situ Concrete", lambda: 1.75 },
    { name: "Steel Reinforced Concrete", lambda: 2.2 },
    { name: "Precast Concrete", lambda: 1.46 },
    { name: "Hollowcore Slab", lambda: 1.23 },
    { name: "Medium-Density Block (1,600kg/m³)", lambda: 0.45 },
    { name: "High-Density Block (1,950kg/m³)", lambda: 1.33 },
    { name: "Aircrete Block (600kg/m³)", lambda: 0.15 },
    { name: "Aircrete Block (460kg/m³)", lambda: 0.11 },
    { name: "Foamed Concrete (600kg/m³)", lambda: 0.18 },
  ],

  "Insulation": [
    { name: "Mineral Wool", lambda: 0.04 },
    { name: "Sheep Wool", lambda: 0.039 },
    { name: "Expanded Polystyrene (EPS)", lambda: 0.036 },
    { name: "Wood Fibre Board", lambda: 0.043 },
    { name: "Extruded polystyrene (XPS)", lambda: 0.032 },
    { name: "Cellulose Fibre (Recycled Paper)", lambda: 0.039 },
    { name: "Cork (Expanded)", lambda: 0.04 },
    { name: "Hemp Fibre", lambda: 0.045 },
    { name: "Mineral Wool Batts", lambda: 0.038 },
    { name: "Phenolic Foam", lambda: 0.021 },
    { name: "Polyisocyanurate (PIR)", lambda: 0.022 },
    { name: "Aerogel Blanket", lambda: 0.015 },
  ],

  "Masonry": [
    { name: "Common Clay Brick (Exposed)", lambda: 0.77 },
    { name: "Common Clay Brick (Protected)", lambda: 0.56 },
    { name: "Engineering Brick (Exposed)", lambda: 1.3 },
    { name: "Engineering Brick (Protected)", lambda: 1.05 },
    { name: "Reclaimed Brick (typical)", lambda: 0.85 },
    { name: "Perforated clay block", lambda: 0.45 },
  ],

  "Stone": [
    { name: "Portland", lambda: 1.7 },
    { name: "Limestone", lambda: 1.7 },
    { name: "Granite", lambda: 3.0 },
    { name: "Slate", lambda: 2.5 },
    { name: "Marble", lambda: 2.7 },
    { name: "Sandstone", lambda: 1.8 },
  ],

  "Metal": [
    { name: "Aluminium", lambda: 205 },
    { name: "Copper", lambda: 385 },
    { name: "Steel (stainless)", lambda: 15 },
    { name: "Steel (carbon / mild steel)", lambda: 50 },
    { name: "Zinc", lambda: 115 },
  ],

  "Timber and Boarding": [
    { name: "Softwood", lambda: 0.13 },
    { name: "Hardwood", lambda: 0.18 },
    { name: "Plywood", lambda: 0.15 },
    { name: "Oriented Strand Board (OSB)", lambda: 0.13 },
    { name: "Medium Density Fibreboard (MDF)", lambda: 0.25 },
    { name: "Chipboard", lambda: 0.15 },
    { name: "Cross Laminated Timber (CLT)", lambda: 0.13 },
    { name: "Cement-bonded Particle Board", lambda: 0.26 },
    { name: "Fibre Cement Board", lambda: 0.30 },
  ],

  "External Finishes": [
    { name: "Cement and Sand Render", lambda: 1.0 },
    { name: "Lime Render", lambda: 0.7 },
    { name: "Silicone Render", lambda: 0.67 },
  ],

  "Internal Finishes": [
    { name: "Plasterboard", lambda: 0.21 },
    { name: "Plaster (Gypsum)", lambda: 0.57 },
    { name: "Plaster (Lime)", lambda: 0.40 },
    { name: "Hardwood Panelling", lambda: 0.13 },
    { name: "Ceramic Tile", lambda: 1.1 },
    { name: "Microcement", lambda: 1.4 },
    { name: "Ceiling Tile", lambda: 0.058 },
  ],

  "Flooring": [
    { name: "Timber Floorboard (Softwood)", lambda: 0.13 },
    { name: "Timber Floorboard (Hardwood)", lambda: 0.18 },
    { name: "Plywood Subfloor", lambda: 0.15 },
    { name: "Carpet", lambda: 0.06 },
    { name: "Polyurethane Foam Underlay", lambda: 0.04 },
    { name: "Rubber Crumb Underlay", lambda: 0.06 },
    { name: "Ceramic Tiles", lambda: 1.1 },
    { name: "Porcelain Tiles", lambda: 1.3 },
    { name: "Natural Stone", lambda: 2.0 },
    { name: "Rubber", lambda: 0.13 },
    { name: "Vinyl", lambda: 0.17 },
    { name: "Cement Sand Screed", lambda: 1.4 },
    { name: "Epoxy Resin", lambda: 0.35 },
  ],

  "Roofs": [
    { name: "Timber (rafters, joists – softwood)", lambda: 0.13 },
    { name: "Plywood / OSB roof deck", lambda: 0.15 },
    { name: "Timber sarking board", lambda: 0.14 },
    { name: "Cement screed (roof fall layer)", lambda: 1.4 },
    { name: "Lightweight screed (with perlite or foam)", lambda: 0.25 },
    { name: "PIR insulation board", lambda: 0.022 },
    { name: "XPS insulation (extruded polystyrene)", lambda: 0.029 },
    { name: "Bituminous felt (2–3 layer)", lambda: 0.17 },
    { name: "EPDM membrane", lambda: 0.25 },
    { name: "PVC / TPO single-ply membrane", lambda: 0.23 },
    { name: "Zinc standing seam roof", lambda: 115 },
    { name: "Aluminium roof sheet", lambda: 205 },
    { name: "Clay roof tile", lambda: 0.85 },
    { name: "Concrete roof tile", lambda: 1.1 },
    { name: "Slate", lambda: 2.5 },
    { name: "Green roof substrate", lambda: 0.4 },
    { name: "Green roof vegetation layer", lambda: 0.3 },
    { name: "Geotextile filter layer", lambda: 0.3 },
    { name: "Blue roof retention chamber water layer (full)", lambda: 0.6 },
  ],
};

// Flattened list for easy lookups
const allMaterials = Object.values(materialsByCategory).flat();

const componentTypesData = {
  "External Wall": { Rse: 0.04, Rsi: 0.13, partLTarget: "0.18" },
  "Internal Wall": { Rse: 0.13, Rsi: 0.13, partLTarget: "N/A" },
  Roof: { Rse: 0.04, Rsi: 0.13, partLTarget: "0.11" },
  "Intermediate Floor": { Rse: 0.11, Rsi: 0.11, partLTarget: "N/A" },
  "Ground Floor": { Rse: 0.17, Rsi: 0.0, partLTarget: "0.18" },
};

const airLayerData = {
  None: { R: 0, description: "No air layer in the construction." },
  Unventilated: { R: 0.18, description: "A sealed air layer with no airflow." },
  "Slightly Ventilated": { R: 0.09, description: "Small openings to outside." },
  "Well Ventilated": { R: 0, description: "Large openings; external layers ignored." },
};

const defaultComponentType = "External Wall";
const storageKeyForProject = (projectId) => `pebbledash:uvalues:v1:${projectId || "no-project"}`;

const createDefaultOuterLayers = () => [
  {
    id: crypto.randomUUID(),
    category: "Masonry",
    materialName: "Common Clay Brick (Exposed)",
    thickness: 102.5,
  },
];

const createDefaultInnerLayers = () => [
  {
    id: crypto.randomUUID(),
    category: "Insulation",
    materialName: "Mineral Wool",
    thickness: 95,
  },
  {
    id: crypto.randomUUID(),
    category: "Internal Finishes",
    materialName: "Plasterboard",
    thickness: 12.5,
  },
];

const createDefaultAirLayer = () => ({ type: "None", thickness: 25 });

const defaultMortarFractionPercent = 7;
const defaultMortarLambda = 0.77;
const defaultWallTieSettings = {
  include: true,
  density: 3,
  diameter: 3.989422,
  lambda: 17,
};

const sanitiseLayers = (rawLayers, fallbackFactory) => {
  const fallback = fallbackFactory();
  if (!Array.isArray(rawLayers) || rawLayers.length === 0) {
    return fallback;
  }

  return rawLayers.map((layer, index) => {
    const candidate = layer && typeof layer === "object" ? layer : {};
    const fallbackLayer = fallback[index] || fallback[0] || {
      category: "Concrete",
      materialName: allMaterials[0]?.name || "In-situ Concrete",
      thickness: 100,
    };

    const category = Object.prototype.hasOwnProperty.call(materialsByCategory, candidate.category)
      ? candidate.category
      : fallbackLayer.category;

    const materials = materialsByCategory[category] || [];
    const fallbackMaterial = materials.some((m) => m.name === fallbackLayer.materialName)
      ? fallbackLayer.materialName
      : materials[0]?.name || fallbackLayer.materialName;

    const materialName = materials.some((m) => m.name === candidate.materialName)
      ? candidate.materialName
      : fallbackMaterial;

    const rawThickness = Number(candidate.thickness);
    const thickness = Number.isFinite(rawThickness) ? rawThickness : fallbackLayer.thickness ?? 0;

    return {
      id: typeof candidate.id === "string" && candidate.id ? candidate.id : crypto.randomUUID(),
      category,
      materialName,
      thickness,
    };
  });
};

const sanitiseAirLayer = (rawAirLayer) => {
  const fallback = createDefaultAirLayer();
  const candidate = rawAirLayer && typeof rawAirLayer === "object" ? rawAirLayer : {};
  const type = Object.prototype.hasOwnProperty.call(airLayerData, candidate.type) ? candidate.type : fallback.type;
  const rawThickness = Number(candidate.thickness);
  const thickness = Number.isFinite(rawThickness) ? rawThickness : fallback.thickness;
  return { type, thickness };
};

/* ----------------------- Page ----------------------- */
export default function UValues() {
  const api = useApi();
  const params = useParams();
  const [sp] = useSearchParams();
  const projectId =
    params.id ||
    sp.get("project_id") ||
    sp.get("projectId") ||
    sp.get("project") ||
    null;
  const rawUsageEventId = sp.get("usage_event_id");
  const { event: historyEvent, loading: historyLoading, error: historyError, usageEventId } =
    useUsageEvent(rawUsageEventId);

  const [project, setProject] = useState(null);
  const [projectLoading, setProjectLoading] = useState(!!projectId);
  const [loadError, setLoadError] = useState("");
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(!projectId);
  const [pickErr, setPickErr] = useState("");

  // Form state (defaults updated to your list)
  const [componentType, setComponentType] = useState(defaultComponentType);

  // Initial outer layer (Masonry brick)
  const [outerLayers, setOuterLayers] = useState(() => createDefaultOuterLayers());

  // Initial inner layers: PIR + Plasterboard
  const [innerLayers, setInnerLayers] = useState(() => createDefaultInnerLayers());

  const [airLayer, setAirLayer] = useState(() => createDefaultAirLayer());

  const [mortarFractionPercent, setMortarFractionPercent] = useState(defaultMortarFractionPercent);
  const [mortarLambda, setMortarLambda] = useState(defaultMortarLambda);
  const [includeWallTies, setIncludeWallTies] = useState(defaultWallTieSettings.include);
  const [wallTieDensity, setWallTieDensity] = useState(defaultWallTieSettings.density);
  const [wallTieDiameter, setWallTieDiameter] = useState(defaultWallTieSettings.diameter);
  const [wallTieLambda, setWallTieLambda] = useState(defaultWallTieSettings.lambda);

  // Live math (hidden until first paid run)
  const [live, setLive] = useState({ uValue: 0, totalResistance: 0, totalThickness: 0 });

  // Result snapshot (what we show after a paid run)
  const [hasRun, setHasRun] = useState(false);
  const [resultSnapshot, setResultSnapshot] = useState(null);
  const [lastInputsJSON, setLastInputsJSON] = useState("");
  const [dirtySinceRun, setDirtySinceRun] = useState(false);

  const [running, setRunning] = useState(false);
  const [serverEcho, setServerEcho] = useState(null);

  const storageKeyRef = useRef(storageKeyForProject(projectId));

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, []);

  const resetToDefaults = useCallback(() => {
    setComponentType(defaultComponentType);
    setOuterLayers(createDefaultOuterLayers());
    setInnerLayers(createDefaultInnerLayers());
    setAirLayer(createDefaultAirLayer());
    setMortarFractionPercent(defaultMortarFractionPercent);
    setMortarLambda(defaultMortarLambda);
    setIncludeWallTies(defaultWallTieSettings.include);
    setWallTieDensity(defaultWallTieSettings.density);
    setWallTieDiameter(defaultWallTieSettings.diameter);
    setWallTieLambda(defaultWallTieSettings.lambda);
    setHasRun(false);
    setResultSnapshot(null);
    setLastInputsJSON("");
    setDirtySinceRun(false);
    setServerEcho(null);
  }, []);

  useEffect(() => {
    let active = true;

    async function loadProject(pid) {
      try {
        setProjectLoading(true);
        setLoadError("");
        const proj = await api(`/projects/${pid}`);
        if (!active) return;
        setProject(proj || null);
      } catch (e) {
        if (!active) return;
        setProject(null);
        setLoadError(toErrorMessage(e) || "Failed to load project");
      } finally {
        if (active) setProjectLoading(false);
      }
    }

    async function loadProjectsList() {
      try {
        setLoadingProjects(true);
        setPickErr("");
        const list = await api(`/projects`);
        if (!active) return;
        setProjects(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!active) return;
        setPickErr(toErrorMessage(e) || "Failed to load projects");
      } finally {
        if (active) setLoadingProjects(false);
      }
    }

    if (projectId) {
      setProjects([]);
      setLoadingProjects(false);
      setPickErr("");
      loadProject(projectId);
    } else if (!usageEventId) {
      setProject(null);
      setProjectLoading(false);
      setLoadError("");
      loadProjectsList();
    } else {
      setProject(null);
      setProjectLoading(false);
      setLoadError("");
      setProjects([]);
      setPickErr("");
      setLoadingProjects(false);
    }

    return () => {
      active = false;
    };
  }, [projectId, usageEventId, api]);

  useEffect(() => {
    storageKeyRef.current = storageKeyForProject(projectId);

    if (typeof window === "undefined" || !window?.localStorage) {
      resetToDefaults();
      return;
    }

    const key = storageKeyRef.current;
    let parsed = null;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        parsed = JSON.parse(raw);
      }
    } catch {
      try {
        window.localStorage.removeItem(key);
      } catch {
        /* ignore storage removal errors */
      }
    }

    if (!parsed) {
      resetToDefaults();
      return;
    }

    if (typeof parsed.componentType === "string" && componentTypesData[parsed.componentType]) {
      setComponentType(parsed.componentType);
    } else {
      setComponentType(defaultComponentType);
    }

    setOuterLayers(sanitiseLayers(parsed.outerLayers, createDefaultOuterLayers));
    setInnerLayers(sanitiseLayers(parsed.innerLayers, createDefaultInnerLayers));
    setAirLayer(sanitiseAirLayer(parsed.airLayer));
    setMortarFractionPercent(
      typeof parsed.mortarFractionPercent === "number" ? parsed.mortarFractionPercent : defaultMortarFractionPercent,
    );
    setMortarLambda(typeof parsed.mortarLambda === "number" ? parsed.mortarLambda : defaultMortarLambda);
    setIncludeWallTies(
      typeof parsed.includeWallTies === "boolean" ? parsed.includeWallTies : defaultWallTieSettings.include,
    );
    setWallTieDensity(
      typeof parsed.wallTieDensity === "number" ? parsed.wallTieDensity : defaultWallTieSettings.density,
    );
    setWallTieDiameter(
      typeof parsed.wallTieDiameter === "number" ? parsed.wallTieDiameter : defaultWallTieSettings.diameter,
    );
    setWallTieLambda(typeof parsed.wallTieLambda === "number" ? parsed.wallTieLambda : defaultWallTieSettings.lambda);

    const snapshot = parsed.resultSnapshot && typeof parsed.resultSnapshot === "object" ? parsed.resultSnapshot : null;
    setResultSnapshot(parsed.hasRun ? snapshot : null);
    setHasRun(Boolean(parsed.hasRun));
    setLastInputsJSON(typeof parsed.lastInputsJSON === "string" ? parsed.lastInputsJSON : "");
    setDirtySinceRun(Boolean(parsed.dirtySinceRun));
    setServerEcho(parsed.serverEcho && typeof parsed.serverEcho === "object" ? parsed.serverEcho : null);
  }, [projectId, resetToDefaults]);

  useEffect(() => {
    if (typeof window === "undefined" || !window?.localStorage) {
      return;
    }

    const key = storageKeyRef.current;
    if (!key) return;

    const payload = {
      componentType,
      outerLayers,
      innerLayers,
      airLayer,
      mortarFractionPercent,
      mortarLambda,
      includeWallTies,
      wallTieDensity,
      wallTieDiameter,
      wallTieLambda,
      hasRun,
      resultSnapshot: hasRun ? resultSnapshot : null,
      lastInputsJSON,
      dirtySinceRun,
      serverEcho,
    };

    try {
      window.localStorage.setItem(key, JSON.stringify(payload));
    } catch {
      /* ignore write failures */
    }
  }, [
    componentType,
    outerLayers,
    innerLayers,
    airLayer,
    mortarFractionPercent,
    mortarLambda,
    includeWallTies,
    wallTieDensity,
    wallTieDiameter,
    wallTieLambda,
    hasRun,
    resultSnapshot,
    lastInputsJSON,
    dirtySinceRun,
    serverEcho,
  ]);

  useEffect(() => {
    if (!usageEventId || !historyEvent) return;
    const details = historyEvent.details || {};
    if (typeof details.componentType === "string" && componentTypesData[details.componentType]) {
      setComponentType(details.componentType);
    }
    if (Array.isArray(details.outerLayers)) {
      setOuterLayers(sanitiseLayers(details.outerLayers, createDefaultOuterLayers));
    }
    if (Array.isArray(details.innerLayers)) {
      setInnerLayers(sanitiseLayers(details.innerLayers, createDefaultInnerLayers));
    }
    if (details.airLayer) {
      setAirLayer(sanitiseAirLayer(details.airLayer));
    }
    setMortarFractionPercent(
      typeof details.mortarFractionPercent === "number" ? details.mortarFractionPercent : defaultMortarFractionPercent,
    );
    setMortarLambda(typeof details.mortarLambda === "number" ? details.mortarLambda : defaultMortarLambda);
    setIncludeWallTies(
      typeof details.includeWallTies === "boolean" ? details.includeWallTies : defaultWallTieSettings.include,
    );
    setWallTieDensity(
      typeof details.wallTieDensity === "number" ? details.wallTieDensity : defaultWallTieSettings.density,
    );
    setWallTieDiameter(
      typeof details.wallTieDiameter === "number" ? details.wallTieDiameter : defaultWallTieSettings.diameter,
    );
    setWallTieLambda(typeof details.wallTieLambda === "number" ? details.wallTieLambda : defaultWallTieSettings.lambda);

    const latestInputsJSON = JSON.stringify({
      componentType: details.componentType,
      airLayer: details.airLayer,
      outerLayers: details.outerLayers,
      innerLayers: details.innerLayers,
      mortarFractionPercent: details.mortarFractionPercent,
      mortarLambda: details.mortarLambda,
      includeWallTies: details.includeWallTies,
      wallTieDensity: details.wallTieDensity,
      wallTieDiameter: details.wallTieDiameter,
      wallTieLambda: details.wallTieLambda,
    });

    const computed = details.computed && typeof details.computed === "object" ? details.computed : null;
    if (computed) {
      setResultSnapshot({
        uValue: computed.uValue,
        totalThickness: computed.totalThicknessM,
        componentType: details.componentType || defaultComponentType,
      });
    }
    setHasRun(true);
    setDirtySinceRun(false);
    setLastInputsJSON(latestInputsJSON);
    setServerEcho(historyEvent.result_json || null);
  }, [usageEventId, historyEvent]);

  // When adding a new layer, default to Concrete → In-situ Concrete
  const createNewLayer = () => ({
    id: crypto.randomUUID(),
    category: "Concrete",
    materialName: "In-situ Concrete",
    thickness: 100,
  });

  /* ---------- helpers for list edits ---------- */
  const updateLayer = (layers, setLayers, id, field, value) => {
    const next = layers.map((layer) => {
      if (layer.id !== id) return layer;
      const upd = { ...layer, [field]: value };
      if (field === "category") {
        const mats = materialsByCategory[value];
        if (mats?.length) upd.materialName = mats[0].name;
      }
      return upd;
    });
    setLayers(next);
  };
  const addLayer = (layers, setLayers) => setLayers([...layers, createNewLayer()]);
  const removeLayer = (layers, setLayers, id) => setLayers(layers.filter((l) => l.id !== id));

  /* ---------- calculations (live, local) ---------- */
  const processedLayers = useMemo(() => {
    const process = (layer) => {
      const m = allMaterials.find((x) => x.name === layer.materialName);
      const lambda = m ? m.lambda : 1;
      const t = Number(layer.thickness || 0) / 1000; // mm -> m
      const rValue = t > 0 && lambda > 0 ? t / lambda : 0;
      return { ...layer, lambda, rValue };
    };
    return {
      outer: outerLayers.map(process),
      inner: innerLayers.map(process),
    };
  }, [outerLayers, innerLayers]);

  useEffect(() => {
    const { Rsi, Rse } = componentTypesData[componentType];
    const airR = airLayerData[airLayer.type].R;

    const sumR = (ls) => ls.reduce((s, x) => s + (Number(x.rValue) || 0), 0);
    const sumT = (ls) => ls.reduce((s, x) => s + (Number(x.thickness) || 0), 0);

    const innerR = sumR(processedLayers.inner);
    const outerR = sumR(processedLayers.outer);

    const innerT = sumT(innerLayers);
    const outerT = sumT(outerLayers);
    const airT = airLayer.type !== "None" ? Number(airLayer.thickness || 0) : 0;

    let totalR = 0;
    if (airLayer.type === "Well Ventilated") {
      totalR = Rsi + 0.13 + innerR; // treat external as still air
    } else {
      totalR = Rsi + Rse + outerR + airR + innerR;
    }

    const totalThicknessM = (outerT + airT + innerT) / 1000;
    const u = totalR > 0 ? 1 / totalR : 0;

    setLive({ uValue: u, totalResistance: totalR, totalThickness: totalThicknessM });
  }, [componentType, processedLayers, airLayer, innerLayers, outerLayers]);

  const currentInputsJSON = useMemo(
    () =>
      JSON.stringify({
        componentType,
        airLayer,
        outerLayers: processedLayers.outer.map(stripIds),
        innerLayers: processedLayers.inner.map(stripIds),
        mortarFractionPercent,
        mortarLambda,
        includeWallTies,
        wallTieDensity,
        wallTieDiameter,
        wallTieLambda,
      }),
    [
      componentType,
      airLayer,
      processedLayers,
      mortarFractionPercent,
      mortarLambda,
      includeWallTies,
      wallTieDensity,
      wallTieDiameter,
      wallTieLambda,
    ],
  );

  useEffect(() => {
    setDirtySinceRun(hasRun && currentInputsJSON !== lastInputsJSON);
  }, [hasRun, currentInputsJSON, lastInputsJSON]);

  /* ---------- Part L target colouring (for snapshot) ---------- */
  const target = parseFloat(componentTypesData[componentType].partLTarget);
  const hasTarget = !Number.isNaN(target);
  const snapshotU = resultSnapshot?.uValue ?? 0;
  const compliant = hasTarget && snapshotU > 0 && snapshotU <= target;

  const uBoxState = hasTarget ? (compliant ? "compliant" : "fail") : "neutral";

  /* ---------- run (freeze results) ---------- */
  async function onRun() {
    try {
      setRunning(true);

      const payload = {
        componentType,
        airLayer,
        outerLayers: processedLayers.outer.map(stripIds),
        innerLayers: processedLayers.inner.map(stripIds),
        mortar_bridging_fraction: mortarFractionPercent / 100,
        mortar_thermal_conductivity_W_mK: mortarLambda,
        include_wall_ties: includeWallTies,
        wall_tie_density_per_m2: wallTieDensity,
        wall_tie_diameter_mm: wallTieDiameter,
        wall_tie_lambda_W_mK: wallTieLambda,
        computed: {
          uValue: live.uValue,
          totalResistance: live.totalResistance,
          totalThicknessM: live.totalThickness,
        },
      };

      const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
      const res = await api(`/calc/u-values${qs}`, { method: "POST", body: payload });

      const snapshot = {
        uValue: Number(res.U_corrected_W_m2K ?? live.uValue),
        uValueUnadjusted: Number(res.U_unadjusted_W_m2K ?? live.uValue),
        uValueMortar: Number(res.U_mortar_adjusted_W_m2K ?? res.U_unadjusted_W_m2K ?? live.uValue),
        wallTieDelta: Number(res.U_wall_ties_delta_W_m2K ?? 0),
        totalThickness: Number(res.total_thickness_m ?? live.totalThickness),
        totalResistance: Number(res.total_resistance_m2K_W ?? live.totalResistance),
        componentType,
        mortarFraction: typeof res.mortar_bridging_fraction === "number"
          ? res.mortar_bridging_fraction
          : mortarFractionPercent / 100,
        mortarLambda: typeof res.mortar_thermal_conductivity_W_mK === "number"
          ? res.mortar_thermal_conductivity_W_mK
          : mortarLambda,
        wallTies: res.wall_ties || {
          include: includeWallTies,
          density_per_m2: wallTieDensity,
          diameter_mm: wallTieDiameter,
          lambda_W_mK: wallTieLambda,
          delta_u_W_m2K: 0,
        },
      };

      trackCalculatorRun("u-values", {
        projectId,
        inputs: payload,
        resultSummary: {
          u_value_unadjusted: round3(snapshot.uValueUnadjusted),
          u_value_corrected: round3(snapshot.uValue),
          wall_tie_delta_W_m2K: round3(snapshot.wallTieDelta),
          total_thickness_m: snapshot.totalThickness,
          component_type: componentType,
        },
      });

      // freeze + show results from this run
      setResultSnapshot(snapshot);
      const latestInputsJSON = JSON.stringify({
        componentType,
        airLayer,
        outerLayers: payload.outerLayers,
        innerLayers: payload.innerLayers,
        mortarFractionPercent,
        mortarLambda,
        includeWallTies,
        wallTieDensity,
        wallTieDiameter,
        wallTieLambda,
      });

      setHasRun(true);
      setLastInputsJSON(latestInputsJSON);
      setDirtySinceRun(false);
      setServerEcho(res);
      toast.success("Calculation complete");

      // log usage_event (non-fatal if it fails)
      try {
        await api(`/usage-events`, {
          method: "POST",
          body: {
            project_id: projectId,
            calc_type: "u-values",
            params: payload,
            result_summary: {
              u_value_unadjusted: round3(snapshot.uValueUnadjusted),
              u_value_mortar: round3(snapshot.uValueMortar),
              u_value_corrected: round3(snapshot.uValue),
              wall_tie_delta_W_m2K: round3(snapshot.wallTieDelta),
              thickness_mm: Math.round(snapshot.totalThickness * 1000),
              component_type: componentType,
              compliant_with_partL: hasTarget ? (snapshot.uValue > 0 && snapshot.uValue <= target) : null,
            },
          },
        });
      } catch {
        // ignore usage event logging failures
      }
    } catch (e) {
      toast.error(e?.message || "Run failed");
    } finally {
      setRunning(false);
    }
  }

  /* ----------------------- UI ----------------------- */
  const showPicker = !projectId && !usageEventId;
  const buildingUseSummary = project?.building_use ? `Building Use: ${project.building_use}` : "";
  const runDisabled =
    showPicker ||
    running ||
    projectLoading ||
    (hasRun && !dirtySinceRun);
  const runLabel = hasRun && !dirtySinceRun ? "No changes" : "Run calculation";
  const historyProject = historyEvent?.project_id
    ? { id: historyEvent.project_id, name: historyEvent.project_name }
    : null;
  const layoutProject = project || historyProject;
  const historySummary = historyEvent
    ? [
        historyEvent.project_name || "Saved run",
        historyEvent.created_at
          ? new Date(historyEvent.created_at).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : null,
      ].filter(Boolean)
    : null;
  const layoutDetails = project ? buildingUseSummary : historySummary;
  const historyBanner = (() => {
    if (!usageEventId) return null;
    if (historyLoading) return "Loading saved calculator run…";
    if (historyError) return `Failed to load saved run: ${historyError}`;
    if (historyEvent) {
      const ts = historyEvent.created_at
        ? new Date(historyEvent.created_at).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })
        : null;
      return ts
        ? `Restored calculator inputs from ${ts}. Update the build-up and rerun if needed.`
        : "Restored calculator inputs from your history.";
    }
    return null;
  })();

  const mortarFraction = resultSnapshot?.mortarFraction ?? mortarFractionPercent / 100;
  const wallTieSettings = resultSnapshot?.wallTies || {
    include: includeWallTies,
    density_per_m2: wallTieDensity,
    diameter_mm: wallTieDiameter,
    lambda_W_mK: wallTieLambda,
    delta_u_W_m2K: resultSnapshot?.wallTieDelta ?? 0,
  };

  return (
    <CalculatorLayout
      title={U_VALUE_CALCULATOR_META.title}
      project={layoutProject}
      projectDetails={layoutDetails}
      costLabel="Included with subscription"
      onRun={showPicker ? null : onRun}
      runLabel={runLabel}
      runDisabled={runDisabled}
      runLoading={running}
      subtitle={showPicker ? U_VALUE_CALCULATOR_META.subtitle : undefined}
    >
      {usageEventId && historyBanner && (
        <div className="pd-inline-alert pd-inline-alert--info">{historyBanner}</div>
      )}

      {showPicker ? (
        <CalculatorSection
          title="Select a project"
          description="Pick a project to run the U-value calculator."
        >
          {pickErr && (
            <div className="pd-alert">
              <strong>Error:</strong> {pickErr}
            </div>
          )}

          {loadingProjects ? (
            <div className="pd-results-placeholder pd-results-placeholder--auto">
              Loading your projects…
            </div>
          ) : projects.length === 0 ? (
            <div className="pd-empty-state">
              <p>You don’t have any projects yet.</p>
              <div className="pd-empty-state__actions">
                <Link to="/projects/new" className="pd-btn pd-btn--primary">
                  + Create new project
                </Link>
              </div>
            </div>
          ) : (
            <div className="pd-project-grid">
              {projects.map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}/calculators/u-values`}
                  className="pd-project-card"
                >
                  <div className="pd-project-card__head">
                    <div>
                      <div className="pd-project-card__title">{p.name}</div>
                      <div className="pd-project-card__meta">
                        {p.postcode || "—"} · {p.local_authority || "—"}
                      </div>
                    </div>
                    {p.building_use && (
                      <span className="pd-pill">{p.building_use}</span>
                    )}
                  </div>
                  <div className="pd-project-card__foot">
                    <span>Floors: {p.floors_count ?? "—"}</span>
                    <span className="pd-project-card__cta">Open calculator</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CalculatorSection>
      ) : (
        <>
          <CalculatorSection
            title="Input parameters"
            description="Set the component type and build the construction from outside to inside."
          >
            {loadError && (
              <div className="pd-alert">
                <strong>Error:</strong> {loadError}
              </div>
            )}

            <div className="pd-input-grid">
              <div className="pd-form-group pd-form-group--full">
                <label htmlFor="uvalue-component-type">Component type</label>
                <select
                  id="uvalue-component-type"
                  value={componentType}
                  onChange={(e) => setComponentType(e.target.value)}
                  className="pd-uvalue__control"
                >
                  {Object.keys(componentTypesData).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            <p className="pd-form-note">
              Part L target U-value: <strong>{componentTypesData[componentType].partLTarget}</strong> W/m²K
            </p>

            <div className="pd-uvalue__layer-stack">
              <LayerSection
                title="Outer leaf"
                layers={processedLayers.outer}
                updateLayer={(id, field, value) => updateLayer(outerLayers, setOuterLayers, id, field, value)}
                addLayer={() => addLayer(outerLayers, setOuterLayers)}
                removeLayer={(id) => removeLayer(outerLayers, setOuterLayers, id)}
              />

              <AirLayerSection airLayer={airLayer} setAirLayer={setAirLayer} />

              <CorrectionsSection
                mortarFractionPercent={mortarFractionPercent}
                setMortarFractionPercent={setMortarFractionPercent}
                mortarLambda={mortarLambda}
                setMortarLambda={setMortarLambda}
                includeWallTies={includeWallTies}
                setIncludeWallTies={setIncludeWallTies}
                wallTieDensity={wallTieDensity}
                setWallTieDensity={setWallTieDensity}
                wallTieDiameter={wallTieDiameter}
                setWallTieDiameter={setWallTieDiameter}
                wallTieLambda={wallTieLambda}
                setWallTieLambda={setWallTieLambda}
              />

              <LayerSection
                title="Inner leaf"
                layers={processedLayers.inner}
                updateLayer={(id, field, value) => updateLayer(innerLayers, setInnerLayers, id, field, value)}
                addLayer={() => addLayer(innerLayers, setInnerLayers)}
                removeLayer={(id) => removeLayer(innerLayers, setInnerLayers, id)}
              />
            </div>

            <div className="pd-uvalue__inline-run">
              <button
                type="button"
                className="pd-btn pd-btn--primary"
                onClick={onRun}
                disabled={runDisabled || typeof onRun !== "function"}
              >
                {running ? "Running…" : runLabel}
              </button>
            </div>
          </CalculatorSection>

          <CalculatorSection
            title="Notes"
            description="Key assumptions that sit behind the estimator."
          >
            <div className="pd-calculator__notes">
              {U_VALUE_CALCULATOR_META.notes.map((note) => (
                <p key={note}>{note}</p>
              ))}
            </div>
          </CalculatorSection>

          <CalculatorSection
            title="Results"
            description="Thermal transmittance and build-up thickness."
          >
            {!hasRun ? (
              <div className="pd-results-placeholder">
                Results will appear here after running the calculator.
              </div>
            ) : (
              <>
                {dirtySinceRun && (
                  <div className="pd-uvalue__notice">
                    Inputs changed since the last run. Re-run to refresh results.
                  </div>
                )}

                <div className="pd-uvalue__results-grid">
                  <div className={`pd-uvalue__result pd-uvalue__result--${uBoxState}`}>
                    <div className="pd-uvalue__result-label">Final corrected U-value (W/m²K)</div>
                    <div className="pd-uvalue__result-value">{round3(resultSnapshot.uValue)}</div>
                  </div>
                  <div className="pd-uvalue__result pd-uvalue__result--secondary">
                    <div className="pd-uvalue__result-label">Unadjusted U-value (W/m²K)</div>
                    <div className="pd-uvalue__result-value">{round3(resultSnapshot.uValueUnadjusted ?? resultSnapshot.uValue)}</div>
                  </div>
                  <div className="pd-uvalue__result pd-uvalue__result--secondary">
                    <div className="pd-uvalue__result-label">Total thickness (mm)</div>
                    <div className="pd-uvalue__result-value">{Math.round((resultSnapshot.totalThickness || 0) * 1000)}</div>
                  </div>
                </div>

                <div className="pd-uvalue__result-breakdown">
                  <p>
                    Unadjusted U value (no mortar, no ties): {" "}
                    <strong>{round3(resultSnapshot.uValueUnadjusted ?? resultSnapshot.uValue)}</strong> W/m²K
                  </p>
                  <p>
                    U value with mortar bridging: <strong>{round3(resultSnapshot.uValueMortar ?? resultSnapshot.uValue)}</strong> W/m²K
                  </p>
                  <p>
                    Wall tie correction: +{round3(resultSnapshot.wallTieDelta ?? 0)} W/m²K
                  </p>
                  <p>
                    Final corrected U value: <strong>{round3(resultSnapshot.uValue)}</strong> W/m²K
                  </p>
                </div>

                <div className="pd-uvalue__export">
                  <DownloadUValueReportBtn
                    project={layoutProject}
                    result={resultSnapshot}
                    componentType={componentType}
                    target={hasTarget ? parseFloat(componentTypesData[componentType].partLTarget) : null}
                    compliant={compliant}
                    airLayer={airLayer}
                    outerLayers={processedLayers.outer}
                    innerLayers={processedLayers.inner}
                    disabled={running}
                    dirty={dirtySinceRun}
                  />
                </div>

                {hasTarget && (
                  <p className="pd-uvalue__compliance">
                    Compliance with Part L target {componentTypesData[componentType].partLTarget}:{" "}
                    {resultSnapshot.uValue > 0 ? (
                      compliant ? (
                        <strong className="pd-uvalue__compliance-status pd-uvalue__compliance-status--ok">Compliant</strong>
                      ) : (
                        <strong className="pd-uvalue__compliance-status pd-uvalue__compliance-status--fail">Not compliant</strong>
                      )
                    ) : (
                      "—"
                    )}
                  </p>
                )}


                <p className="pd-form-note">Estimator only — verify with a qualified professional.</p>
              </>
            )}
          </CalculatorSection>
        </>
      )}
    </CalculatorLayout>
  );
}

/* ----------------------- UI bits ----------------------- */
function LayerSection({ title, layers, updateLayer, addLayer, removeLayer }) {
  return (
    <div className="pd-uvalue__layer-card">
      <div className="pd-uvalue__layer-head">
        <div className="pd-h4">{title}</div>
        <button type="button" className="pd-btn pd-btn--ghost pd-btn--compact" onClick={addLayer}>
          Add layer
        </button>
      </div>
      <div className="pd-uvalue__layer-scroll">
        <div className="pd-uvalue__layer-grid">
          <div className="pd-uvalue__layer-header">
            <div>Category</div>
            <div>Material</div>
            <div className="pd-uvalue__layer-label--right">Thickness (mm)</div>
            <div className="pd-uvalue__layer-label--right">λ</div>
            <div aria-hidden="true" />
          </div>
          <div className="pd-uvalue__layer-rows">
            {layers.map((layer) => (
              <LayerRow key={layer.id} layer={layer} onUpdate={updateLayer} onRemove={removeLayer} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function LayerRow({ layer, onUpdate, onRemove }) {
  return (
    <div className="pd-uvalue__layer-row">
      <select
        value={layer.category}
        onChange={(e) => onUpdate(layer.id, "category", e.target.value)}
        className="pd-uvalue__control"
      >
        {Object.keys(materialsByCategory).map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>

      <select
        value={layer.materialName}
        onChange={(e) => onUpdate(layer.id, "materialName", e.target.value)}
        className="pd-uvalue__control"
      >
        {materialsByCategory[layer.category]?.map((m) => (
          <option key={m.name} value={m.name}>{m.name}</option>
        ))}
      </select>

      <input
        type="number"
        step="0.1"
        min="0"
        value={layer.thickness}
        onChange={(e) => onUpdate(layer.id, "thickness", parseFloat(e.target.value) || 0)}
        className="pd-uvalue__control pd-uvalue__control--number"
      />

      <div className="pd-uvalue__lambda">{Number.isFinite(layer.lambda) ? layer.lambda.toFixed(3) : "-"}</div>

      <button
        type="button"
        onClick={() => onRemove(layer.id)}
        className="pd-btn pd-btn--ghost pd-btn--compact"
      >
        Remove
      </button>
    </div>
  );
}

function CorrectionsSection({
  mortarFractionPercent,
  setMortarFractionPercent,
  mortarLambda,
  setMortarLambda,
  includeWallTies,
  setIncludeWallTies,
  wallTieDensity,
  setWallTieDensity,
  wallTieDiameter,
  setWallTieDiameter,
  wallTieLambda,
  setWallTieLambda,
}) {
  const handlePercent = (e) => setMortarFractionPercent(Math.max(0, Math.min(50, parseFloat(e.target.value) || 0)));
  const handleLambda = (e) => setMortarLambda(parseFloat(e.target.value) || 0);
  const handleInclude = (e) => setIncludeWallTies(e.target.checked);

  return (
    <div className="pd-uvalue__layer-card pd-uvalue__corrections-card">
      <div className="pd-uvalue__layer-head">
        <div className="pd-h4">Corrections</div>
        <div className="pd-uvalue__layer-subtitle">Mortar bridging and wall tie adjustments.</div>
      </div>
      <div className="pd-uvalue__corrections-grid">
        <div className="pd-uvalue__corrections-column">
          <div className="pd-h5">Mortar bridging</div>
          <div className="pd-form-group">
            <label htmlFor="uvalue-mortar-fraction">Mortar bridging fraction</label>
            <div className="pd-uvalue__input-with-unit">
              <input
                id="uvalue-mortar-fraction"
                type="number"
                min="0"
                max="50"
                step="0.1"
                value={mortarFractionPercent}
                onChange={handlePercent}
                className="pd-uvalue__control pd-uvalue__control--number"
              />
              <span className="pd-uvalue__unit">%</span>
            </div>
            <p className="pd-form-note">Default 7% mortar area for masonry.</p>
          </div>
          <div className="pd-form-group">
            <label htmlFor="uvalue-mortar-lambda">Mortar thermal conductivity</label>
            <div className="pd-uvalue__input-with-unit">
              <input
                id="uvalue-mortar-lambda"
                type="number"
                min="0"
                step="0.01"
                value={mortarLambda}
                onChange={handleLambda}
                className="pd-uvalue__control pd-uvalue__control--number"
              />
              <span className="pd-uvalue__unit">W/mK</span>
            </div>
          </div>
        </div>

        <div className="pd-uvalue__corrections-column">
          <div className="pd-h5">Wall ties</div>
          <div className="pd-form-group pd-form-group--checkbox">
            <label htmlFor="uvalue-include-ties" className="pd-checkbox">
              <input
                id="uvalue-include-ties"
                type="checkbox"
                checked={includeWallTies}
                onChange={handleInclude}
              />
              <span>Include wall ties in U value correction</span>
            </label>
          </div>

          {includeWallTies && (
            <div className="pd-uvalue__wall-tie-grid">
              <div className="pd-form-group">
                <label htmlFor="uvalue-tie-density">Wall tie density</label>
                <div className="pd-uvalue__input-with-unit">
                  <input
                    id="uvalue-tie-density"
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={wallTieDensity}
                    onChange={(e) => setWallTieDensity(parseFloat(e.target.value) || 0)}
                    className="pd-uvalue__control pd-uvalue__control--number"
                  />
                  <span className="pd-uvalue__unit">ties/m²</span>
                </div>
              </div>
              <div className="pd-form-group">
                <label htmlFor="uvalue-tie-diameter">Wall tie diameter</label>
                <div className="pd-uvalue__input-with-unit">
                  <input
                    id="uvalue-tie-diameter"
                    type="number"
                    min="0.1"
                    step="0.01"
                    value={wallTieDiameter}
                    onChange={(e) => setWallTieDiameter(parseFloat(e.target.value) || 0)}
                    className="pd-uvalue__control pd-uvalue__control--number"
                  />
                  <span className="pd-uvalue__unit">mm</span>
                </div>
              </div>
              <div className="pd-form-group">
                <label htmlFor="uvalue-tie-lambda">Wall tie thermal conductivity</label>
                <div className="pd-uvalue__input-with-unit">
                  <input
                    id="uvalue-tie-lambda"
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={wallTieLambda}
                    onChange={(e) => setWallTieLambda(parseFloat(e.target.value) || 0)}
                    className="pd-uvalue__control pd-uvalue__control--number"
                  />
                  <span className="pd-uvalue__unit">W/mK</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AirLayerSection({ airLayer, setAirLayer }) {
  const handleType = (e) => setAirLayer((p) => ({ ...p, type: e.target.value }));
  const handleThk = (e) => setAirLayer((p) => ({ ...p, thickness: parseFloat(e.target.value) || 0 }));

  return (
    <div className="pd-uvalue__layer-card pd-uvalue__air-card">
      <div className="pd-uvalue__layer-head">
        <div className="pd-h4">Air layer</div>
      </div>
      <div className="pd-uvalue__air-grid">
        <div className="pd-form-group">
          <label htmlFor="uvalue-air-type">Type</label>
          <select
            id="uvalue-air-type"
            value={airLayer.type}
            onChange={handleType}
            className="pd-uvalue__control"
          >
            {Object.keys(airLayerData).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="pd-form-group">
          <label htmlFor="uvalue-air-thickness">Thickness (mm)</label>
          <input
            id="uvalue-air-thickness"
            type="number"
            step="0.1"
            min="0"
            value={airLayer.thickness}
            onChange={handleThk}
            disabled={airLayer.type === "None"}
            className="pd-uvalue__control pd-uvalue__control--number"
          />
        </div>
      </div>
      <p className="pd-form-note pd-uvalue__air-description">{airLayerData[airLayer.type].description}</p>
    </div>
  );
}

/* ----------------------- tiny utils ----------------------- */
const stripIds = (layer) => {
  const { id, ...rest } = layer;
  void id;
  return rest;
};
const round3 = (n) => (Number.isFinite(n) ? n.toFixed(3) : "0.000");
